import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPostForm } from '../../api/client';
import { fmtTL } from '../../format';

export default function NewReceipt() {
  const nav = useNavigate();
  const [types, setTypes] = useState<string[]>([]);
  const [receiptType, setReceiptType] = useState('');
  const [docNo, setDocNo] = useState('');
  const [employeeNote, setEmployeeNote] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amountNet, setAmountNet] = useState('');
  const [vatRate, setVatRate] = useState('20');
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    apiGet<{ types: string[] }>('/api/receipt-types').then((d) => {
      setTypes(d.types);
      setReceiptType(d.types[0] ?? '');
    });
  }, []);

  const { vatAmount, total } = useMemo(() => {
    const net = parseFloat(amountNet || '0');
    const rate = parseFloat(vatRate || '0');
    if (!isFinite(net) || !isFinite(rate)) return { vatAmount: 0, total: 0 };
    const v = Math.round(net * rate) / 100;
    return { vatAmount: v, total: Math.round((net + v) * 100) / 100 };
  }, [amountNet, vatRate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submittingRef.current) return;
    setErr(null);
    if (!file) {
      setErr('Lütfen fiş görselini yükleyin');
      return;
    }
    submittingRef.current = true;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('receipt_type', receiptType);
      form.append('document_no', docNo);
      form.append('employee_note', employeeNote);
      form.append('company_name', companyName);
      form.append('receipt_date', date);
      form.append('amount_net', amountNet);
      form.append('vat_rate', vatRate);
      form.append('image', file);
      await apiPostForm('/api/receipts', form);
      nav('/fisler');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold mb-4">Yeni Fiş</h1>
      <form onSubmit={onSubmit} className="card p-6 space-y-4">
        <fieldset disabled={busy} className="space-y-4 disabled:opacity-60">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Fiş Türü</label>
            <select
              required
              className="input"
              value={receiptType}
              onChange={(e) => setReceiptType(e.target.value)}
            >
              {types.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Tarih</label>
            <input
              type="date"
              required
              className="input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Belge / Fiş No</label>
            <input
              type="text"
              className="input"
              value={docNo}
              onChange={(e) => setDocNo(e.target.value)}
              placeholder="örn. 42"
            />
          </div>
          <div className="md:col-span-2">
            <label className="label">Şirket Adı</label>
            <input
              required
              className="input"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="örn. ABC Restoran"
            />
          </div>
          <div>
            <label className="label">Tutar (KDV Hariç, ₺)</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              required
              className="input"
              value={amountNet}
              onChange={(e) => setAmountNet(e.target.value)}
            />
          </div>
          <div>
            <label className="label">KDV Oranı (%)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              required
              className="input"
              value={vatRate}
              onChange={(e) => setVatRate(e.target.value)}
            />
          </div>
        </div>

        <div className="bg-slate-50 rounded-md p-3 text-sm grid grid-cols-3 gap-2">
          <div>
            <div className="text-slate-500">KDV Tutarı</div>
            <div className="font-semibold">{fmtTL(vatAmount)}</div>
          </div>
          <div>
            <div className="text-slate-500">Net</div>
            <div className="font-semibold">{fmtTL(parseFloat(amountNet || '0'))}</div>
          </div>
          <div>
            <div className="text-slate-500">Toplam (Brüt)</div>
            <div className="font-semibold text-brand-700">{fmtTL(total)}</div>
          </div>
        </div>

        <div>
          <label className="label">Fiş Notu (Opsiyonel)</label>
          <textarea
            className="input min-h-[80px]"
            value={employeeNote}
            onChange={(e) => setEmployeeNote(e.target.value)}
            placeholder="Fiş hakkında eklemek istediğiniz not..."
          />
        </div>

        <div>
          <label className="label">Fiş Görseli (JPG, PNG, WEBP veya PDF — maks 10 MB)</label>
          <input
            type="file"
            required
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
          />
        </div>

        {err && <div className="text-sm text-red-600">{err}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={() => nav('/fisler')}>
            İptal
          </button>
          <button className="btn-primary" disabled={busy}>
            {busy ? 'Gönderiliyor…' : 'Gönder'}
          </button>
        </div>
        </fieldset>
      </form>
    </div>
  );
}
